<template>
  <div ref="mapContainer" class="z-0 size-full" />
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import * as L from 'leaflet';
import 'leaflet-imageoverlay-rotated';
import { getLayers, useLocalStorageLayer } from '@/utils/map/layer';
import { useMapResize } from '@/utils/map/resize';
import { mapCenter } from '@/config/constants';
import { hasValidCorners } from '@/utils/map/overlayCorners';

const props = defineProps({
  imageUrl: {
    type: String,
    default: null,
  },
  corners: {
    type: Object,
    default: null,
  },
  opacity: {
    type: Number,
    default: 1,
  },
});

const emit = defineEmits(['update:corners']);

const mapContainer = ref(null);
const map = ref(null);
let cleanupResize = null;
let overlayLayer = null;
let markers = [];

function patchRotatedOverlayZoomSafety() {
  const rotatedOverlayPrototype = L?.ImageOverlay?.Rotated?.prototype;
  if (!rotatedOverlayPrototype || rotatedOverlayPrototype.__gpxSafeAnimateZoomPatched) return;

  const originalAnimateZoom = rotatedOverlayPrototype._animateZoom;
  if (typeof originalAnimateZoom !== 'function') return;

  rotatedOverlayPrototype._animateZoom = function safeAnimateZoom(...args) {
    if (!this?._map || !this?._image) return;
    return originalAnimateZoom.apply(this, args);
  };
  rotatedOverlayPrototype.__gpxSafeAnimateZoomPatched = true;
}

const { id: savedLayerId } = useLocalStorageLayer();
patchRotatedOverlayZoomSafety();

function getDefaultCorners() {
  if (!map.value) return null;
  const center = map.value.getCenter();
  const bounds = map.value.getBounds();
  const latSpan = (bounds.getNorth() - bounds.getSouth()) * 0.15;
  const lngSpan = (bounds.getEast() - bounds.getWest()) * 0.15;

  return {
    topLeft: { lat: center.lat + latSpan, lng: center.lng - lngSpan },
    topRight: { lat: center.lat + latSpan, lng: center.lng + lngSpan },
    bottomLeft: { lat: center.lat - latSpan, lng: center.lng - lngSpan },
  };
}

function createCornerMarker(latLng, label) {
  const marker = L.marker(latLng, {
    draggable: true,
    icon: L.divIcon({
      className: 'overlay-corner-handle',
      html: `<div class="overlay-handle-dot" title="${label}"></div>`,
      iconSize: [16, 16],
      iconAnchor: [8, 8],
    }),
  });
  return marker;
}

function clearOverlay() {
  if (map.value) {
    map.value.stop();
  }
  if (overlayLayer && map.value) {
    map.value.removeLayer(overlayLayer);
    overlayLayer = null;
  }
  markers.forEach((m) => {
    m.off('drag').off('dragend');
    if (map.value) map.value.removeLayer(m);
  });
  markers = [];
}

function renderOverlay() {
  clearOverlay();
  if (!map.value || !props.imageUrl) return;

  const corners = props.corners || getDefaultCorners();
  if (!hasValidCorners(corners)) return;

  const topLeft = L.latLng(corners.topLeft.lat, corners.topLeft.lng);
  const topRight = L.latLng(corners.topRight.lat, corners.topRight.lng);
  const bottomLeft = L.latLng(corners.bottomLeft.lat, corners.bottomLeft.lng);

  overlayLayer = L.imageOverlay.rotated(
    props.imageUrl,
    topLeft,
    topRight,
    bottomLeft,
    { opacity: props.opacity, interactive: false },
  );
  overlayLayer.addTo(map.value);

  const markerTopLeft = createCornerMarker(topLeft, 'Top Left');
  const markerTopRight = createCornerMarker(topRight, 'Top Right');
  const markerBottomLeft = createCornerMarker(bottomLeft, 'Bottom Left');

  markers = [markerTopLeft, markerTopRight, markerBottomLeft];
  markers.forEach((m) => m.addTo(map.value));

  function onDrag() {
    const tl = markerTopLeft.getLatLng();
    const tr = markerTopRight.getLatLng();
    const bl = markerBottomLeft.getLatLng();

    if (overlayLayer) {
      overlayLayer.reposition(tl, tr, bl);
    }
  }

  function onDragEnd() {
    const tl = markerTopLeft.getLatLng();
    const tr = markerTopRight.getLatLng();
    const bl = markerBottomLeft.getLatLng();

    emit('update:corners', {
      topLeft: { lat: tl.lat, lng: tl.lng },
      topRight: { lat: tr.lat, lng: tr.lng },
      bottomLeft: { lat: bl.lat, lng: bl.lng },
    });
  }

  markers.forEach((m) => {
    m.on('drag', onDrag);
    m.on('dragend', onDragEnd);
  });

  if (!props.corners) {
    emit('update:corners', corners);
  }
}

onMounted(() => {
  const layers = getLayers();
  const initialLayer =
    savedLayerId === 'satellite' ? layers.satellite : layers.map;

  map.value = L.map(mapContainer.value, {
    center: mapCenter,
    zoom: 5,
    layers: [initialLayer],
  });

  L.control
    .layers({
      Map: layers.map,
      Satellite: layers.satellite,
    })
    .addTo(map.value);

  cleanupResize = useMapResize(map, mapContainer);

  if (props.corners) {
    const bounds = L.latLngBounds([
      [props.corners.topLeft.lat, props.corners.topLeft.lng],
      [props.corners.topRight.lat, props.corners.topRight.lng],
      [props.corners.bottomLeft.lat, props.corners.bottomLeft.lng],
    ]);
    map.value.fitBounds(bounds, { padding: [80, 80] });
  }

  if (props.imageUrl) {
    renderOverlay();
  }
});

onBeforeUnmount(() => {
  clearOverlay();
  if (cleanupResize) cleanupResize();
  if (map.value) {
    try {
      map.value.stop();
      map.value.remove();
    } catch {
      // Map may already be partially destroyed during navigation
    }
    map.value = null;
  }
});

watch(
  () => props.imageUrl,
  (url) => {
    if (url) {
      nextTick(() => renderOverlay());
    } else {
      clearOverlay();
    }
  },
);

watch(
  () => props.opacity,
  (val) => {
    if (overlayLayer) {
      overlayLayer.setOpacity(val);
    }
  },
);
</script>

<style>
.overlay-corner-handle {
  background: none;
  border: none;
}
.overlay-handle-dot {
  width: 16px;
  height: 16px;
  background: #3b82f6;
  border: 2px solid white;
  border-radius: 50%;
  cursor: grab;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
}
.overlay-handle-dot:active {
  cursor: grabbing;
}
</style>
